import React, { useState, useEffect } from 'react';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import ContractsPresentational from './ContractsPresentational';
import contractsAPIs, { deleteFeeFile } from '../../lib/fetch/contractsAPIs';
import axios from 'axios';
import { profileScoreFormatter } from '../../lib/helpers/profileScoreFormatter';
import { getSignedUrl } from '../../lib/fetch/files';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const Contracts = ({ profile }) => {
  const [contracts, setContracts] = useState([]);
  const [formattedCSVContracts, setFormattedCSVContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [{ newToken, newIsAdmin } = {}] = useAuth();
  const [hasImportedContracts, setHasImportedContracts] = useState(false);
  const [
    contractsImportFeedbackData,
    setContractsImportFeedbackData,
  ] = useState({});
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();
  const [profileScore, setProfileScore] = useState({});
  const [totPod, setTotPod] = useState(0);
  const [totPdr, setTotPdr] = useState(0);

  const [hasImportedIafPolis, setHasImportedIafPolis] = useState(false);
  const [iafPolisImportFeedbackData, setIafPolisImportFeedbackData] = useState(
    {}
  );

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    year: NumberParam,
    pod_pdr: withDefault(StringParam, ''),
    condo_admin_cf: withDefault(StringParam, ''),
    condo_vat_cf: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    address: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    customerType: withDefault(StringParam, ''),
    managementStatus: withDefault(StringParam, ''),
    contractSigningYear: NumberParam,
    contractSigningMonth: NumberParam,
    contractType: withDefault(StringParam, ''),
    partnerEonType: withDefault(StringParam, ''),
    iafPolis: withDefault(StringParam, ''),
  });

  const {
    year,
    limit,
    offset,
    admin,
    condo_vat_cf,
    condo_admin_cf,
    supplier,
    pod_pdr,
    customer,
    customerType,
    managementStatus,
    contractSigningYear,
    contractSigningMonth,
    contractType,
    partnerEonType,
    iafPolis,
  } = query;

  const toggleShowImportFeedback = () => setHasImportedContracts((v) => !v);
  const toggleShowImportIafPolisFeedback = () =>
    setHasImportedIafPolis((value) => !value);

  function onFilter(queryDiff) {
    if (
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.pod_pdr && queryDiff.pod_pdr !== pod_pdr) || // first search
      (pod_pdr && queryDiff.pod_pdr === '') ||
      (queryDiff.contractSigningYear &&
        queryDiff.contractSigningYear !== contractSigningYear) || // first search
      (contractSigningYear && queryDiff.contractSigningYear === '') || // search deleted
      (queryDiff.contractSigningMonth &&
        queryDiff.contractSigningMonth !== contractSigningMonth) || // first search
      (contractSigningMonth && queryDiff.contractSigningMonth === '') || // search deleted
      (queryDiff.customer && queryDiff.customer !== customer) || // first search
      (customer && queryDiff.customer === '') || // search deleted
      (queryDiff.customerType && queryDiff.customerType !== customerType) || // first search
      (customerType && queryDiff.customerType === '') || // search deleted
      (queryDiff.contractType && queryDiff.contractType !== contractType) || // first search
      (contractType && queryDiff.contractType === '') || // search deleted
      (queryDiff.partnerEonType &&
        queryDiff.partnerEonType !== partnerEonType) || // first selection
      (partnerEonType && queryDiff.partnerEonType === '') || // selection cleared
      (queryDiff.managementStatus &&
        queryDiff.managementStatus !== managementStatus) || // first search
      (managementStatus && queryDiff.managementStatus === '') || // search deleted
      (queryDiff.condo_vat_cf && queryDiff.condo_vat_cf !== condo_vat_cf) || // first search
      (condo_vat_cf && queryDiff.condo_vat_cf === '') || // search deleted
      (queryDiff.condo_admin_cf &&
        queryDiff.condo_admin_cf !== condo_admin_cf) || // first search
      (condo_admin_cf && queryDiff.condo_admin_cf === '') ||
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') ||
      (queryDiff.iafPolis && queryDiff.iafPolis !== iafPolis) || // first search
      (iafPolis && queryDiff.iafPolis === '') ||
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchContracts = async (query) => {
    showLoading();
    const {
      data,
      total: _total,
      formattedCSVContracts,
      totPod,
      totPdr,
    } = await contractsAPIs.find({ newToken, query });
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/scores`,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    setTotal(Number(_total || 0));
    setTotPod(totPod);
    setTotPdr(totPdr);

    setContracts(data);
    setProfileScore(
      profileScoreFormatter(response.data, response.data.type || 'auto')
    );
  };

  const fetchContractsForExport = async (query) => {
    const {
      data,
      total: _total,
      formattedCSVContracts,
    } = await contractsAPIs.findForExport({ newToken, query });
    setFormattedCSVContracts(formattedCSVContracts);
  };

  const onImportContract = async (file) => {
    showLoading();
    const { error, data } =
      profile.polis === true
        ? await contractsAPIs.importContractsPolis({
            newToken,
            file,
          })
        : await contractsAPIs.importContracts({
            newToken,
            file,
          });
    hideLoading();
    toggleShowImportFeedback();
    setContractsImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchContracts(query);
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  const onImportIafPolis = async (file) => {
    showLoading();
    const { error, data } = await contractsAPIs.importIafPolis({
      newToken,
      file,
      profileId: profile._id,
    });
    hideLoading();
    toggleShowImportIafPolisFeedback();
    setIafPolisImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchContracts(query);
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function handleDelete({ contractId, key, isReport }) {
    showLoading();

    const { error, data } = await deleteFeeFile({
      contractId,
      key,
      newToken,
      isReport,
    });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchContracts(query);
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  useEffect(() => {
    fetchContracts(query);
    fetchContractsForExport(query);
  }, [
    year,
    condo_vat_cf,
    pod_pdr,
    condo_admin_cf,
    supplier,
    offset,
    limit,
    customer,
    customerType,
    managementStatus,
    contractSigningYear,
    contractSigningMonth,
    contractType,
    partnerEonType,
    iafPolis,
  ]);

  return (
    <ContractsPresentational
      data={contracts}
      formattedData={formattedCSVContracts}
      profile={profile}
      profileScore={profileScore}
      query={query}
      total={total}
      totPod={totPod}
      totPdr={totPdr}
      onFilter={onFilter}
      onImportContract={onImportContract}
      toggleShowImportFeedback={toggleShowImportFeedback}
      contractsImportFeedbackData={contractsImportFeedbackData}
      hasImportedContracts={hasImportedContracts}
      onImportIafPolis={onImportIafPolis}
      toggleShowImportIafPolisFeedback={toggleShowImportIafPolisFeedback}
      iafPolisImportFeedbackData={iafPolisImportFeedbackData}
      hasImportedIafPolis={hasImportedIafPolis}
      onStatusChanged={() => fetchContracts(query)}
      handleFileListView={handleFileListView}
      handleDelete={handleDelete}
    />
  );
};

export default Contracts;
